import { parseCalendarEventOptions, calendarEventId, calendarEtag, type CalendarEventOptions } from "../../supabase/functions/_shared/calendarDomain";
import type { PendingConnectorWrite } from "@/lib/connectorPendingWrite";
import type { LocalAgendaActionPrefill } from "@/lib/agendaExecutor";

export type CalendarProvider = "google_calendar" | "outlook";
export type CalendarActionKind = "search" | "create" | "update" | "delete";
export type CalendarWriteAction = "create_event" | "update_event" | "delete_event";

export interface CalendarActionPrefill {
  action: CalendarActionKind;
  provider: "auto" | CalendarProvider;
  query: string;
  action_id: string;
  expected_user_id?: string;
  expected_session_id?: string;
  event_id?: string;
  title?: string;
  start?: string;
  end?: string;
  all_day?: boolean;
  time_zone?: string;
  location?: string;
  description?: string;
  // Prepared by the capture boundary, never copied from model arguments.
  vyv_draft?: LocalAgendaActionPrefill;
}

export interface CalendarEventDetail {
  id: string;
  etag: string;
  title: string;
  start: string;
  end: string;
  all_day: boolean;
  time_zone: string;
  location?: string;
  description?: string;
  recurring: boolean;
  attendee_count: number;
  writable: boolean;
  write_restriction?: string;
  advanced_writable?: boolean;
  event_options?: CalendarEventOptions;
  series_id?: string;
}

/** A locator only. The Espelho reads its own owner-scoped server receipt. */
export interface MirrorCalendarReceiptReference {
  provider: CalendarProvider;
  capability: CalendarWriteAction;
  idempotency_key: string;
  action_id: string;
  userId: string;
  sessionId: string;
}
export interface MirrorCalendarOrigin { userId: string; sessionId: string; actionId: string }

const object = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export function matchesMirrorCalendarReceiptOrigin(value: unknown, origin: MirrorCalendarOrigin): value is MirrorCalendarReceiptReference {
  return object(value) && !!origin.userId && !!origin.sessionId && !!origin.actionId &&
    value.userId === origin.userId && value.sessionId === origin.sessionId && value.action_id === origin.actionId &&
    ["google_calendar", "outlook"].includes(String(value.provider)) &&
    ["create_event", "update_event", "delete_event"].includes(String(value.capability)) &&
    value.idempotency_key === "mirror_calendar_" + origin.actionId + "_" + value.capability + "_0";
}

export function mirrorCalendarReceiptReference(entry: PendingConnectorWrite, origin: MirrorCalendarOrigin): MirrorCalendarReceiptReference | null {
  const body = entry.body;
  const value = { provider: body.provider, capability: body.action, idempotency_key: body.idempotency_key,
    action_id: origin.actionId, userId: origin.userId, sessionId: origin.sessionId };
  if (entry.group !== "calendar" || entry.status !== "succeeded" || entry.ownerId !== origin.userId ||
    entry.key !== origin.userId + ":calendar" || !object(body.mirror_context) ||
    body.mirror_context.session_id !== origin.sessionId || body.mirror_context.action_id !== origin.actionId ||
    !matchesMirrorCalendarReceiptOrigin(value, origin)) return null;
  return value;
}

export function parseCalendarEvent(value: unknown): CalendarEventDetail | null {
  if (!object(value) || typeof value.id !== "string" || !value.id ||
    typeof value.etag !== "string" || typeof value.title !== "string" ||
    typeof value.start !== "string" || typeof value.end !== "string" ||
    typeof value.all_day !== "boolean" || typeof value.time_zone !== "string" ||
    typeof value.writable !== "boolean" || typeof value.recurring !== "boolean" ||
    !Number.isInteger(value.attendee_count) || Number(value.attendee_count) < 0) return null;
  try {
    if (value.advanced_writable === true) {
      calendarEventId(value.id); calendarEtag(value.etag);
      const options = parseCalendarEventOptions(value.event_options, false);
      if (options.attendees.length !== value.attendee_count ||
        (options.scope !== 'single') !== value.recurring ||
        (options.scope === 'series') !== !!options.recurrence ||
        (options.scope === 'occurrence' && (!value.series_id || calendarEventId(value.series_id) === value.id)) ||
        (options.scope !== 'occurrence' && value.series_id !== undefined)) return null;
      return {...value,event_options:options} as unknown as CalendarEventDetail;
    }
    if (value.advanced_writable !== undefined && value.advanced_writable !== false) return null;
    return value as unknown as CalendarEventDetail;
  } catch { return null; }
}

export const calendarWriteAction = (kind: Exclude<CalendarActionKind, "search">): CalendarWriteAction => kind + "_event" as CalendarWriteAction;

export function dateAfter(value: string, days: number): string {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(value)) return "";
  const date = new Date(value + "T12:00:00Z");
  if (!Number.isFinite(date.getTime()) || date.toISOString().slice(0, 10) !== value) return "";
  date.setUTCDate(date.getUTCDate() + days);
  return date.toISOString().slice(0, 10);
}

export function calendarInputDate(value?: string): string {
  if (!value) return "";
  const date = new Date(value);
  if (!Number.isFinite(date.getTime())) return "";
  const pad = (part: number) => String(part).padStart(2, "0");
  return date.getFullYear() + "-" + pad(date.getMonth() + 1) + "-" + pad(date.getDate()) +
    "T" + pad(date.getHours()) + ":" + pad(date.getMinutes());
}

/** Avoid dropping provider seconds when a user edits only the event title. */
export function calendarInstant(value: string, original?: string): string {
  if (original && /T.*(?:Z|[+-]\d{2}:\d{2})$/.test(original) && calendarInputDate(original) === value) return original;
  if (!/^\d{4}-\d{2}-\d{2}T\d{2}:\d{2}$/.test(value)) return "";
  const date = new Date(value);
  return Number.isFinite(date.getTime()) && calendarInputDate(date.toISOString()) === value ? date.toISOString() : "";
}
export { parseCalendarEventOptions };
export type { CalendarEventOptions };
